/**
 * Class description
 *
 * Created: 2018-06-13
 * @author jonas
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {IReelStoppedData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SpinReelEvent} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelEvent";
import {
    INearWinReelDividerConfig,
    NearWinReelDivider
} from "@nolimitcity/slot-game/bin/game/reeldivider/NearWinReelDivider";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Linear, Timeline, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {MaskCreator} from "../../utils/MaskCreator";
import {DungeonQuestHelper} from "../bigSymbols/DungeonQuestHelper";
import {DungeonQuest} from "../DungeonQuest";
import {DungeonQuestGameAssets} from "../DungeonQuestGameAssets";
import {DungeonQuestGameConfig, DungeonQuestSymbols} from "../DungeonQuestGameConfig";
import {DungeonQuestModeData} from "../server/DungeonQuestModeData";


export class DungeonQuestNearWinReelDivider extends NearWinReelDivider {
    private _maskGfx:PIXI.Graphics;
    private _dividerLoops:(TimelineMax | null)[];
    private _dividerSwoosh:TimelineSprite[];
    private _swooshContainer:PIXI.Container;
    private _reelHighlight:PIXI.Sprite;

    constructor(config:INearWinReelDividerConfig) {
        super(config);
    }


    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, SpinReelEvent.REEL_STOP_SPIN_BOUNCE_STARTED, (event:GameEvent) => this.onReelBounce(event.params[0]));
    }

    protected initAnimations():void {
        super.initAnimations();

        this._reelHighlight = new PIXI.Sprite(GameResources.getTextures(DungeonQuestGameAssets.NEAR_WIN_REEL_HIGHLIGHT)[0]);
        this._reelHighlight.anchor.set(0.5, 0);
        this._reelHighlight.scale.set(1.4, 1);
        this._reelHighlight.position.set(0, -10);
        this._reelHighlight.blendMode = PIXI.BLEND_MODES.ADD;
        this._reelHighlight.alpha = 0;

        this.addChild(this._reelHighlight);

        this._dividerLoops = [];
        this._dividerSwoosh = [];
        this._swooshContainer = new PIXI.Container();

        if(this._nearWinDividers != null && this._nearWinDividers[0] != null) {
            const divider:TimelineSprite = <TimelineSprite>this._nearWinDividers[0];
            divider.scale.set(-1, 1);
        }

        for(let i:number = 0; i < this._nearWinDividers.length; i++) {
            let div:TimelineSprite | null = this._nearWinDividers[i];
            if(div) {
                div.blendMode = PIXI.BLEND_MODES.ADD;
                this.addChild(div);
                div.alpha = 0;
                div.anchor.set(0.5, 0);
                div.scale.set(1, 1);

                const swoosh:TimelineSprite = this.createSwoosh(div);
                div.addChild(swoosh);
                this._dividerSwoosh.push(swoosh);
            }

            this._dividerLoops.push(null);
        }

        this.addChild(this._swooshContainer);

        this._maskGfx = MaskCreator.createSymbolAreaMaskGraphics();
        this._maskGfx.scale.x = 2;
        this._maskGfx.position.x = -GameConfig.instance.REEL_AREA_WIDTH / 2;
        this.addChild(this._maskGfx);
        this.mask = this._maskGfx;

    }

    private startLoop(dividerIndex:number, divider:TimelineSprite, color:number) {
        const newLoop:TimelineMax = new TimelineMax({repeat : -1});
        const divAnimation = divider.getAnimation();
        const swooshAnimation = this.getSwooshAnimation(dividerIndex, color);
        newLoop.add([
            divAnimation,
            swooshAnimation
        ]);
        newLoop.time(Math.random() * newLoop.duration());
        this._dividerLoops[dividerIndex] = newLoop;
    }

    private stopLoop(dividerIndex:number) {
        const loop:TimelineMax | null = this._dividerLoops[dividerIndex];
        if(loop && loop.isActive()) {
            loop.pause();
            this._dividerLoops[dividerIndex] = null;
        }
    }

    protected playNearWinSound(reelId:number):void {
        DungeonQuest.soundPlayer.startAnticipationSound(reelId);
    }

    private onReelBounce(stopData:IReelStoppedData) {
        DungeonQuest.soundPlayer.stopAnticipationSound(stopData.reelId, stopData.quickStop);
    }

    protected stopNearWinSound(stopData:IReelStoppedData):void {
        DungeonQuest.soundPlayer.stopAnticipationSound(stopData.reelId, stopData.quickStop);
    }


    protected getDividerAnimation(divider:TimelineSprite):Timeline {
        const sym:string = DungeonQuestModeData.instance.transformSymbol ? DungeonQuestModeData.instance.transformSymbol : DungeonQuestSymbols.S;
        const color:number = DungeonQuestHelper.getSymbolColor(sym);
        const divIndex = this._nearWinDividers.indexOf(divider);
        divider.tint = color;

        let highlightPosition = divider.x - DungeonQuestGameConfig.instance.SYMBOL_WIDTH * 0.5;
        let targetAlpha = 1;
        /*
                if(divIndex == 5) {
                    targetAlpha = 0;
                }
        */

        const tl:TimelineLite = new TimelineLite();

        tl.add(() => this.startLoop(divIndex, divider, color));
        tl.add(new TweenLite(divider, 0.5, {alpha : targetAlpha, ease : Linear.easeNone}));

        const reelHighLightAnim = new TimelineLite();
        reelHighLightAnim.add(new TweenLite(this._reelHighlight, 0.1, {alpha : 0, ease : Linear.easeNone}));
        reelHighLightAnim.add([
            () => this._reelHighlight.tint = divider.tint,
            () => this._reelHighlight.position.set(highlightPosition, -10),
        ]);
        reelHighLightAnim.add(new TweenLite(this._reelHighlight, 0.25, {alpha : 1, ease : Linear.easeNone}));

        tl.add(reelHighLightAnim, 0);
        return tl;
    }

    protected getDividerEndAnimation(divider:TimelineSprite):Timeline {
        const divIndex = this._nearWinDividers.indexOf(divider);
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            new TweenLite(divider, 0.5, {alpha : 0}),
            new TweenLite(this._reelHighlight, 0.5, {alpha : 0})
        ]);
        tl.add(() => this.stopLoop(divIndex));

        return tl;
    }

    private getSwooshAnimation(dividerIndex:number, color:number) {
        const swoosh:TimelineSprite = this._dividerSwoosh[dividerIndex];
        swoosh.tint = color;
        return swoosh.getAnimation();

    }

    private createSwoosh(divider:TimelineSprite):TimelineSprite {
        const swoosh:TimelineSprite = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.NEAR_WIN_DIVIDER_SWOOSH));
        swoosh.blendMode = PIXI.BLEND_MODES.ADD;
        swoosh.anchor.set(0.5, 0);
        return swoosh;
    }


}